type NodeId = string

type ChildMap = Map<NodeId, Set<NodeId>>

export type NodeConnections = {
  children: Set<NodeId>
  parents: Set<NodeId>
  descendants: Set<NodeId>
  ancestors: Set<NodeId>
}

export type NodeHierarchyMap = Map<NodeId, NodeConnections>

export function createNodeHierarchyMap(childMap: ChildMap): NodeHierarchyMap {
  const parentMap = invertChildMap(childMap)

  const allNodeIds = new Set<NodeId>([...childMap.keys(), ...parentMap.keys()])

  // Caches for memoization
  const descendantsCache: Map<NodeId, Set<NodeId>> = new Map()
  const ancestorsCache: Map<NodeId, Set<NodeId>> = new Map()

  const fullConnectionMap: NodeHierarchyMap = new Map()

  for (const nodeId of allNodeIds) {
    fullConnectionMap.set(nodeId, {
      children: childMap.get(nodeId) || new Set(),
      parents: parentMap.get(nodeId) || new Set(),
      descendants: computeDescendants(nodeId, childMap, new Set(), descendantsCache),
      ancestors: computeDescendants(nodeId, parentMap, new Set(), ancestorsCache)
    })
  }

  return fullConnectionMap
}

function invertChildMap(childMap: ChildMap): ChildMap {
  const parentMap: ChildMap = new Map()

  for (const [parentId, children] of childMap.entries()) {
    for (const childId of children) {
      if (!parentMap.has(childId)) {
        parentMap.set(childId, new Set())
      }
      parentMap.get(childId)!.add(parentId)
    }
  }

  return parentMap
}

function computeDescendants(
  nodeId: NodeId,
  childMap: ChildMap,
  alreadyProcessing: Set<NodeId>,
  cacheOfAlreadyResolvedNodes: Map<NodeId, Set<NodeId>>
): Set<NodeId> {
  if (cacheOfAlreadyResolvedNodes.has(nodeId)) {
    return cacheOfAlreadyResolvedNodes.get(nodeId)!
  }

  if (alreadyProcessing.has(nodeId)) {
    // Cycle detected, return empty set to prevent infinite recursion
    return new Set()
  }

  alreadyProcessing.add(nodeId)

  const descendants = new Set<NodeId>()

  for (const childId of childMap.get(nodeId) || new Set()) {
    descendants.add(childId)

    const childDescendants = computeDescendants(
      childId,
      childMap,
      alreadyProcessing,
      cacheOfAlreadyResolvedNodes
    )

    for (const desc of childDescendants) {
      descendants.add(desc)
    }
  }

  alreadyProcessing.delete(nodeId)
  cacheOfAlreadyResolvedNodes.set(nodeId, descendants)

  return descendants
}
